import ProductPricingFields from '../../components/ProductPricingFields';
import { resolveProductPricing } from '../../utils/productPricing.mjs';
import { parseAliExpressProductId } from '../../utils/aliExpressUrl.mjs';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Card, Alert, Spinner, Badge, Tabs, Tab, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { quickImportProduct, replicateProductToProduction } from '../../actions/productActions';
import { PRODUCT_QUICK_IMPORT_RESET } from '../../constants/productConstants';
import { parseAliHtml, parseDhgateHtml } from '../../utils/aliHtmlParser';
import { fetchRootCategories } from '../../slices/categorySlice';
import Message from '../../components/Message';

const normalizeCategoryName = (s = '') =>
  String(s).toLowerCase().trim().replace(/[^a-z0-9\s&]/g, '').replace(/\s+/g, ' ');

const matchOneName = (aliName, options) => {
  const norm = normalizeCategoryName(aliName);
  if (!norm) return null;
  const exact = options.find((o) => normalizeCategoryName(o.name) === norm);
  if (exact) return exact;
  return options.find((o) => {
    const on = normalizeCategoryName(o.name);
    return on && (norm.includes(on) || on.includes(norm));
  }) || null;
};

// Best-effort match of an AliExpress category against the store's own
// (currently root-level-only) categories. AliExpress's leaf category is
// usually far more specific than anything the store has ("Mats & Pads"),
// so this walks the ancestor path from most specific to broadest — e.g.
// "Home & Garden > Kitchen,Dining & Bar > ... > Mats & Pads" — trying each
// segment until one matches an existing store category, favoring a precise
// match if the store ever grows deeper categories, but still catching the
// common case where only the broad root ("Home & Garden") matches.
// No fuzzy/typo tolerance; a miss just leaves the category unset rather than
// guessing wrong.
const findBestCategoryMatch = (aliPath, options) => {
  const segments = Array.isArray(aliPath) ? [...aliPath].reverse() : [aliPath];
  for (const segment of segments) {
    const match = matchOneName(segment, options);
    if (match) return match;
  }
  return null;
};

const createImportId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
  return `quick-import-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const ProductQuickImportScreen = () => {
  const [inputMode, setInputMode] = useState('aliexpress');
  const [parseMode, setParseMode] = useState('aliexpress');
  const [fetchParserMode, setFetchParserMode] = useState('aliexpress');
  const [htmlInput, setHtmlInput] = useState('');
  const [dhgateProductInfoInput, setDhgateProductInfoInput] = useState('');
  const [dhgateDescriptionInput, setDhgateDescriptionInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [liveProductId, setLiveProductId] = useState('');
  const [purchaseLink, setPurchaseLink] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [importId, setImportId] = useState(createImportId);
  const [createInProduction, setCreateInProduction] = useState(false);
  const [pricing, setPricing] = useState({ pricingMethod: 'manual', costPrice: '', profitMargin: '' });
  const applyParsedProduct = (data) => {
    setParsedData(data);
    setImportId(data ? createImportId() : createImportId());
    setPricing((current) => ({
      ...current,
      costPrice: data?.costPrice ?? '',
      costSource: data?.costSource || 'admin',
    }));
  };
  const [parsing, setParsing] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [showManualPriceModal, setShowManualPriceModal] = useState(false);
  const [manualPriceInput, setManualPriceInput] = useState('');
  const [manualPriceError, setManualPriceError] = useState('');
  const [generateWithAI, setGenerateWithAI] = useState(true);
  const [manualSeededRetry, setManualSeededRetry] = useState(true);
  const isAiGeneratingRef = useRef(false);
  const [, setAiTriggerCount] = useState(0); // tiny render trigger for ref changes
  // Safe wrapper to avoid runtime errors if setter is unexpectedly not a function
  const safeSetAiTriggerCount = (updater) => {
    if (typeof setAiTriggerCount === 'function') {
      try {
        setAiTriggerCount(updater);
      } catch (e) {
        console.warn('Breadcrumb: setAiTriggerCount threw', e);
      }
    } else {
      console.warn('Breadcrumb: setAiTriggerCount is not a function', setAiTriggerCount);
    }
  };
  const [aiError, setAiError] = useState(null);
  const [aliCategoryHint, setAliCategoryHint] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  const productQuickImport = useSelector((state) => state.productQuickImport || {});
  const { loading, error, success, product, uploadStats, productionRetryLoading, productionRetryError } = productQuickImport;
  const production = productQuickImport.production;

  const userLogin = useSelector((state) => state.user || {});
  const { userInfo } = userLogin;
  const categories = useSelector((state) => state.categories || {});
  const rootCategoryIds = categories.roots || [];
  const categoryMap = categories.byId || {};
  const categoryOptions = rootCategoryIds
    .map((categoryId) => categoryMap[categoryId])
    .filter(Boolean);

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      history.push('/login');
    }
  }, [history, userInfo]);

  useEffect(() => {
    dispatch(fetchRootCategories());
  }, [dispatch]);

  useEffect(() => {
    if (success && product && production?.success !== false) {
      const createdProductId = product._id || product.product?._id;
      if (!createdProductId) return;

      // Reset state and redirect to edit screen
      setTimeout(() => {
        dispatch({ type: PRODUCT_QUICK_IMPORT_RESET });
        history.push(`/admin/product/${createdProductId}/edit`);
      }, 2000);
    }
  }, [success, product, production, history, dispatch]);

  const handleParse = () => {
    setParsing(true);
    applyParsedProduct(null);
    setWarnings([]);

    try {
      const result =
        parseMode === 'dhgate'
          ? parseDhgateHtml(dhgateProductInfoInput, dhgateDescriptionInput)
          : parseAliHtml(htmlInput);
      applyParsedProduct(result);
      setWarnings(result.warnings || []);
    } catch (error) {
      setWarnings(['Failed to parse HTML. Make sure you copied the complete product page.']);
    } finally {
      setParsing(false);
    }
  };

  const normalizeManualPrice = (value) => {
    const cleaned = String(value || '').replace(/[^\d.,-]/g, '').replace(/,/g, '');
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const mergeScrapeMetadata = (parsed, scrapeResponse = {}) => {
    const parserSignals = parsed?.meta?.challengeSignals || [];
    const scrapeSignals = scrapeResponse?.challengeSignals || [];
    const challengeSignals = Array.from(new Set([...parserSignals, ...scrapeSignals]));
    const challenged = Boolean(parsed?.challenged || scrapeResponse?.challenged || challengeSignals.length > 0);
    const requiresManualPrice = Boolean(parsed?.requiresManualPrice || (challenged && (parsed?.price == null)));
    const scrapeDiagnostics = scrapeResponse?.scrapeDiagnostics || {};

    return {
      ...parsed,
      challenged,
      requiresManualPrice,
      meta: {
        ...(parsed?.meta || {}),
        source: parsed?.meta?.source || (fetchParserMode === 'dhgate' ? 'dhgate' : 'aliexpress'),
        challengeSignals,
        scrapeDiagnostics,
      },
    };
  };

  const handleFetchUrl = async () => {
    if (!urlInput || !/^https?:\/\//i.test(urlInput)) {
      setWarnings(['Please enter a valid http/https URL'])
      return
    }
    setParsing(true)
    applyParsedProduct(null)
    setWarnings([])
    try {
      const res = await fetch('/api/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo?.token}`,
        },
        body: JSON.stringify({
          url: urlInput,
          manualSeededRetry: manualSeededRetry && fetchParserMode === 'aliexpress',
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setWarnings([data.message || 'Failed to fetch URL'])
        setParsing(false)
        return
      }
      const html = data.html || ''
      // Choose parser based on current parseMode
      const parsed = fetchParserMode === 'dhgate' ? parseDhgateHtml(html, '') : parseAliHtml(html)
      const merged = mergeScrapeMetadata(parsed, data)
      applyParsedProduct(merged)
      setWarnings(merged.warnings || [])
    } catch (err) {
      setWarnings([String(err.message || err) || 'Fetch error'])
    } finally {
      setParsing(false)
    }
  }

  const handleFetchLiveProduct = async () => {
    const resolved = parseAliExpressProductId(liveProductId);
    if (!resolved.ok) {
      setWarnings(["Couldn't find a product ID in that link. Paste a full AliExpress product URL or its numeric ID."]);
      return;
    }
    const productId = resolved.id;
    setParsing(true);
    applyParsedProduct(null);
    setWarnings([]);
    try {
      const base = process.env.NODE_ENV === 'production' ? '/api/aliexpress/product' : '/api/dev-proxy/aliexpress/product';
      const res = await fetch(`${base}/${productId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo?.token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404 && data.message === 'Not connected') {
        setWarnings(['AliExpress is not connected. Connect it from the product list page first.']);
        return;
      }
      if (!res.ok) {
        setWarnings([data.message || 'Failed to fetch AliExpress product']);
        return;
      }
      applyParsedProduct(data);
      setWarnings(data.warnings || []);
      setPurchaseLink(data.purchaseLink || '');
      setAliCategoryHint(null);

      const aliCategoryId = data.meta?.aliExpressCategoryId;
      if (aliCategoryId) {
        try {
          const catBase = process.env.NODE_ENV === 'production' ? '/api/aliexpress/category-name' : '/api/dev-proxy/aliexpress/category-name';
          const catRes = await fetch(`${catBase}/${aliCategoryId}`, {
            headers: { Authorization: `Bearer ${userInfo?.token}` },
          });
          const catData = await catRes.json().catch(() => ({}));
          if (catRes.ok && catData.name) {
            const aliLabel = Array.isArray(catData.path) ? catData.path.join(' > ') : catData.name;
            const match = findBestCategoryMatch(Array.isArray(catData.path) && catData.path.length ? catData.path : catData.name, categoryOptions);
            if (match) {
              setSelectedCategory(match.name);
              setAliCategoryHint(`AliExpress category "${aliLabel}" matched to "${match.name}"`);
            } else {
              // No existing store category matches — still populate the field with
              // AliExpress's own category name rather than leaving it blank/Uncategorized.
              setSelectedCategory(catData.name);
              setAliCategoryHint(`AliExpress category "${aliLabel}" — no matching store category, using "${catData.name}" as-is`);
            }
          }
        } catch {
          // Non-critical: category auto-match is a convenience, not required for import.
        }
      }
    } catch (err) {
      setWarnings([String(err.message || err) || 'Fetch error']);
    } finally {
      setParsing(false);
    }
  };

  const handleClear = () => {
    setHtmlInput('');
    setDhgateProductInfoInput('');
    setDhgateDescriptionInput('');
    setPurchaseLink('');
    setSelectedCategory('');
    setAliCategoryHint(null);
    applyParsedProduct(null);
    setWarnings([]);
    setShowManualPriceModal(false);
    setManualPriceInput('');
    setManualPriceError('');
    setCreateInProduction(false);
  };

  const canParse = inputMode === 'dhgate'
    ? Boolean(dhgateProductInfoInput.trim())
    : Boolean(htmlInput.trim());

  const resolvedLiveProduct = parseAliExpressProductId(liveProductId);

  const scrapeDiagnostics = parsedData?.meta?.scrapeDiagnostics || null;
  const usedSeededSession = Boolean(scrapeDiagnostics?.storageStateLoaded);
  const sessionStrategy = scrapeDiagnostics?.sessionStrategy || '';
  const navigationStrategy = scrapeDiagnostics?.navigationStrategy || '';

  const handleImport = (priceOverride = null) => {
    const normalizedPriceOverride =
      priceOverride &&
      typeof priceOverride === 'object' &&
      (typeof priceOverride.preventDefault === 'function' || priceOverride.nativeEvent || priceOverride.target)
        ? null
        : priceOverride;

    if (!parsedData) return;

    if (pricing.pricingMethod !== 'margin' && parsedData.requiresManualPrice && normalizedPriceOverride == null) {
      setManualPriceInput(parsedData.price != null ? String(parsedData.price) : '');
      setManualPriceError('');
      setShowManualPriceModal(true);
      return;
    }

    const normalizedPurchaseLink = purchaseLink.trim();
    if (normalizedPurchaseLink && !/^https?:\/\//i.test(normalizedPurchaseLink)) {
      setWarnings(['Purchase link must be a valid http/https URL.']);
      return;
    }

    let resolvedPricing;
    try { resolvedPricing = resolveProductPricing({ ...pricing, price: normalizedPriceOverride != null ? normalizedPriceOverride : parsedData.price, skus: parsedData.skus || [] }); }
    catch (error) { setWarnings([error.message]); return; }
    const resolvedPrice = resolvedPricing.price;
    if (parsedData.requiresManualPrice && !Number.isFinite(resolvedPrice)) {
      setWarnings(['Manual price entry is required before creating this product.']);
      return;
    }

    const doImport = (finalDescription) => {
      dispatch(
        quickImportProduct({
          title: parsedData.title,
          description: finalDescription,
          ...resolvedPricing,
          price: resolvedPrice,
          category: selectedCategory,
          purchaseLink: normalizedPurchaseLink,
          gallery: parsedData.gallery,
          variantProps: parsedData.variantProps,
          skus: resolvedPricing.skus,
          video: parsedData.video,
          suggestedFolderName: parsedData.suggestedFolderName,
          createInProduction,
          importId,
          challenged: parsedData.challenged,
          requiresManualPrice: parsedData.requiresManualPrice,
          priceSource: parsedData.requiresManualPrice ? 'manual' : parsedData.priceSource,
          meta: parsedData.meta,
        })
      );
    };

    // AliExpress live-API imports already have a real title + description from
    // AliExpress itself — use both as source material for the AI rewrite rather
    // than only filling in a gap, since the raw AliExpress copy is often messy.
    const isAliExpressLive = parsedData.meta?.source === 'aliexpress-live-api';
    const hasExistingDescription = Boolean(parsedData.description && parsedData.description.trim());
    if (generateWithAI && (isAliExpressLive || !hasExistingDescription)) {
      setAiError(null)
      console.debug('Breadcrumb: AI generation start')
      isAiGeneratingRef.current = true
      safeSetAiTriggerCount((t) => t + 1);
      (async () => {
        try {
          const res = await fetch('/api/ai/generate-description', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userInfo?.token}`,
            },
            body: JSON.stringify({
              title: parsedData.title,
              images: parsedData.gallery || [],
              context: isAliExpressLive && parsedData.description
                ? `Use this original AliExpress product description as source material for details and tone: ${parsedData.description}`
                : undefined,
            }),
          })
          const data = await res.json()
          if (!res.ok) {
            throw new Error(data.message || 'AI generation failed')
          }
          const description = data.description || ''
          setParsedData((prev) => ({ ...prev, description }))
          doImport(description)
        } catch (err) {
          setAiError(String(err.message || err))
          // proceed with empty description
          doImport(parsedData.description || '')
        } finally {
          isAiGeneratingRef.current = false
          safeSetAiTriggerCount((t) => t + 1)
          console.debug('Breadcrumb: AI generation end')
        }
      })()
      return
    }

    doImport(parsedData.description)
  };

  const handleManualPriceConfirm = () => {
    const parsed = normalizeManualPrice(manualPriceInput);
    if (parsed == null) {
      setManualPriceError('Enter a valid price greater than 0.');
      return;
    }
    setManualPriceError('');
    setShowManualPriceModal(false);
    handleImport(parsed);
  };

  return (
    <Container>
      <Row className='my-4'>
        <Col>
          <Link to='/admin/productlist' className='btn btn-light my-3'>
            ← Back to Products
          </Link>
          <h1>Quick Product Import</h1>
          <p className='text-muted'>
            Choose a parser source, paste HTML, then create a disabled product for review
          </p>
        </Col>
      </Row>

      {/* Instructions */}
      <Row className='mb-4'>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Instructions</Card.Title>
              <ol>
                <li>Select parser source: AliExpress or DHgate</li>
                <li>Copy required HTML block(s) from source page</li>
                <li>Paste below and click "Parse HTML"</li>
                <li>Review extracted data</li>
                <li>Click "Create Product" (product will be disabled by default)</li>
              </ol>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* HTML Input */}
      <Row className='mb-4'>
        <Col>
          <Tabs
            id='parser-mode-tabs'
            activeKey={inputMode}
            onSelect={(key) => {
              const nextMode = key || 'aliexpress';
              setInputMode(nextMode);
              if (nextMode === 'aliexpress' || nextMode === 'dhgate') {
                setParseMode(nextMode);
              }
              applyParsedProduct(null);
              setWarnings([]);
            }}
            className='mb-3'
          >
            <Tab eventKey='fetch' title='Fetch URL'>
              <Form.Group controlId='urlInput'>
                <Form.Label>Product URL to fetch:</Form.Label>
                <Form.Control
                  type='url'
                  placeholder='https://www.aliexpress.com/item/...'
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                />
              </Form.Group>
              <Form.Group className='mt-2'>
                <Form.Label>Parser</Form.Label>
                <Form.Control as='select' value={fetchParserMode} onChange={(e) => setFetchParserMode(e.target.value)}>
                  <option value='aliexpress'>AliExpress</option>
                  <option value='dhgate'>DHgate</option>
                </Form.Control>
              </Form.Group>
              {fetchParserMode === 'aliexpress' && (
                <Form.Check
                  type='checkbox'
                  id='manualSeededRetry'
                  className='mt-3'
                  label='If challenged, retry with the seeded AliExpress browser session'
                  checked={manualSeededRetry}
                  onChange={(e) => setManualSeededRetry(e.target.checked)}
                />
              )}
              <div className='mt-3'>
                <Button variant='primary' onClick={handleFetchUrl} disabled={parsing}>
                  {parsing ? (<><Spinner animation='border' size='sm' /> Fetching...</>) : '🔗 Fetch & Parse'}
                </Button>
              </div>
            </Tab>
            <Tab eventKey='live-api' title='Live API'>
              <Form.Group controlId='liveProductId'>
                <Form.Label>AliExpress product link (or ID):</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='https://www.aliexpress.us/item/3256809689915943.html or 3256809689915943'
                  value={liveProductId}
                  onChange={(e) => setLiveProductId(e.target.value)}
                  isInvalid={Boolean(liveProductId.trim()) && !resolvedLiveProduct.ok}
                />
                <Form.Control.Feedback type='invalid'>
                  Couldn't find a product ID in that link. Paste a full AliExpress product URL or its numeric ID.
                </Form.Control.Feedback>
                <Form.Text className='text-muted'>
                  {resolvedLiveProduct.ok
                    ? `Detected product ID: ${resolvedLiveProduct.id}`
                    : 'Fetches live product data via the connected AliExpress account and previews it below — no HTML paste needed.'}
                </Form.Text>
              </Form.Group>
              <div className='mt-3'>
                <Button variant='primary' onClick={handleFetchLiveProduct} disabled={parsing || !resolvedLiveProduct.ok}>
                  {parsing ? (<><Spinner animation='border' size='sm' /> Fetching...</>) : '🔌 Fetch from AliExpress'}
                </Button>
              </div>
            </Tab>
            <Tab eventKey='aliexpress' title='AliExpress Parser'>
              <Form.Group controlId='htmlInput'>
                <Form.Label>Paste Product HTML Here:</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={10}
                  placeholder='<div class="pdp-info">...'
                  value={htmlInput}
                  onChange={(e) => setHtmlInput(e.target.value)}
                />
              </Form.Group>
            </Tab>
            <Tab eventKey='dhgate' title='DHgate Parser'>
              <Form.Group controlId='dhgateProductInfoInput'>
                <Form.Label>Product Info HTML (class="JRka56I"):</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={8}
                  placeholder='<div class="JRka56I">...'
                  value={dhgateProductInfoInput}
                  onChange={(e) => setDhgateProductInfoInput(e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId='dhgateDescriptionInput' className='mt-3'>
                <Form.Label>Product Description HTML (class="GncWtgN"):</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={8}
                  placeholder='<div class="GncWtgN">...'
                  value={dhgateDescriptionInput}
                  onChange={(e) => setDhgateDescriptionInput(e.target.value)}
                />
              </Form.Group>
            </Tab>
          </Tabs>
          <Form.Group controlId='purchaseLink' className='mt-3'>
            <Form.Label>Purchase Link (Optional):</Form.Label>
            <Form.Control
              type='url'
              placeholder='https://www.aliexpress.com/item/...'
              value={purchaseLink}
              onChange={(e) => setPurchaseLink(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId='category' className='mt-3'>
            <Form.Label>Category:</Form.Label>
            <Form.Control
              as='select'
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value=''>Uncategorized (Default)</option>
              {categoryOptions.map((category) => (
                <option key={category._id} value={category.name}>
                  {category.name}
                </option>
              ))}
              {selectedCategory && !categoryOptions.some((c) => c.name === selectedCategory) && (
                <option value={selectedCategory}>{selectedCategory} (from AliExpress)</option>
              )}
            </Form.Control>
            {aliCategoryHint && (
              <Form.Text className='text-muted'>{aliCategoryHint}</Form.Text>
            )}
          </Form.Group>
          <Card className='mt-3'>
            <Card.Body>
              <Card.Title>Pricing</Card.Title>
              <ProductPricingFields
                value={{ ...pricing, skus: parsedData?.skus || [] }}
                deferCostError={!parsedData}
                onChange={({ skus: nextSkus, ...patch }) => {
                  setPricing((current) => ({ ...current, ...patch }));
                  if (nextSkus && parsedData) {
                    setParsedData((current) => ({ ...current, skus: nextSkus }));
                  }
                }}
              />
              {!parsedData && pricing.pricingMethod === 'margin' && (
                <Form.Text className='text-muted'>
                  Enter the profit margin now. Fetch or parse the product next to load its supplier cost and calculate the selling price.
                </Form.Text>
              )}
            </Card.Body>
          </Card>
          <div className='mt-3'>
            <Button variant='primary' onClick={handleParse} disabled={!canParse || parsing}>
              {parsing ? (
                <>
                  <Spinner animation='border' size='sm' /> Parsing...
                </>
              ) : (
                '🔍 Parse HTML'
              )}
            </Button>{' '}
            <Button variant='secondary' onClick={handleClear}>
              🗑️ Clear
            </Button>
          </div>
        </Col>
      </Row>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Row className='mb-4'>
          <Col>
            <Alert variant='warning'>
              <Alert.Heading>⚠️ Warnings</Alert.Heading>
              <ul>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Error Message */}
      {error && (
        <Row className='mb-4'>
          <Col>
            <Message variant='danger'>{error}</Message>
          </Col>
        </Row>
      )}

      {/* Success Message */}
      {success && uploadStats && (
        <Row className='mb-4'>
          <Col>
            <Alert variant='success'>
              <Alert.Heading>✅ Product Created Successfully!</Alert.Heading>
              <p>"{product?.name || product?.product?.name}" has been created in DISABLED mode.</p>
              {production?.success && (
                <p className='mb-2 text-success'>Production database: created successfully{production.alreadyExisted ? ' (existing import reused)' : ''}.</p>
              )}
              {production?.success === false && (
                <Alert variant='warning'>
                  Production database: {production.message}. The local product was created; retry production creation using the same import.
                  <div className='mt-2'>
                    <Button
                      size='sm'
                      variant='warning'
                      disabled={productionRetryLoading}
                      onClick={() => dispatch(replicateProductToProduction(product?._id || product?.product?._id))}
                    >
                      {productionRetryLoading ? 'Retrying production...' : 'Retry production creation'}
                    </Button>
                  </div>
                  {productionRetryError && <div className='mt-2'>{productionRetryError}</div>}
                </Alert>
              )}
              {(product?.purchaseLink || product?.product?.purchaseLink) && (
                <p className='mb-2'>
                  <strong>Purchase Link:</strong>{' '}
                  <a
                    href={product?.purchaseLink || product?.product?.purchaseLink}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {product?.purchaseLink || product?.product?.purchaseLink}
                  </a>
                </p>
              )}
              <hr />
              <p className='mb-0'>
                <strong>Upload Stats:</strong>
                <br />
                Images uploaded: {uploadStats.imagesUploaded}/{uploadStats.imagesUploaded + uploadStats.imagesFailed} ✓
                <br />
                Video uploaded: {uploadStats.videoUploaded ? 'Yes' : 'No'}
                <br />
                Cloudinary folder: {uploadStats.cloudinaryFolder}
              </p>
              <p className='mt-3'>Redirecting to edit screen...</p>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Preview */}
      {parsedData && !success && (
        <Row className='mb-4'>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>📦 Parsed Data Preview</Card.Title>
                <hr />

                {/* Image Preview */}
                {parsedData.gallery && parsedData.gallery[0] && (
                  <div className='mb-3'>
                    <img
                      src={parsedData.gallery[0]}
                      alt='Product'
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                  </div>
                )}

                {/* Title */}
                <p>
                  <strong>Title:</strong> {parsedData.title}
                  {parsedData.meta?.source && (
                    <Badge bg='secondary' className='ms-2'>
                      {String(parsedData.meta.source).toUpperCase()}
                    </Badge>
                  )}
                  {parsedData.challenged && (
                    <Badge bg='warning' text='dark' className='ms-2'>
                      CHALLENGED
                    </Badge>
                  )}
                  {usedSeededSession && (
                    <Badge bg='success' className='ms-2'>
                      SEEDED SESSION
                    </Badge>
                  )}
                </p>

                {scrapeDiagnostics && inputMode === 'fetch' && (
                  <Alert variant={usedSeededSession && !parsedData.challenged ? 'success' : 'secondary'}>
                    <strong>Scrape diagnostics:</strong>{' '}
                    {sessionStrategy ? `session=${sessionStrategy}; ` : ''}
                    {navigationStrategy ? `navigation=${navigationStrategy}; ` : ''}
                    {scrapeDiagnostics.headless === false ? 'headed; ' : 'headless; '}
                    {scrapeDiagnostics.searchFallbackAttempted ? 'search fallback attempted; ' : ''}
                    {scrapeDiagnostics.searchFallbackMatchedResult ? 'exact search match found; ' : ''}
                    {scrapeDiagnostics.manualSeededRetryAttempted ? 'manual-seeded retry attempted; ' : ''}
                    {scrapeDiagnostics.challengeSeenDuringRun ? 'challenge seen during scrape; ' : 'no challenge detected during scrape; '}
                    {scrapeDiagnostics.storageStateLoaded ? 'stored session loaded.' : 'no stored session loaded.'}
                    {scrapeDiagnostics.searchFallbackReason ? ` reason=${scrapeDiagnostics.searchFallbackReason};` : ''}
                  </Alert>
                )}

                {parsedData.challenged && (
                  <Alert variant='warning'>
                    Supplier session is challenge-blocked. Title and media were recovered, but price must be entered manually before creating the product.
                  </Alert>
                )}

                {/* Price */}
                <p>
                  <strong>Imported price:</strong>{' '}
                  {parsedData.requiresManualPrice ? (
                    <Badge bg='warning' text='dark'>Manual entry required</Badge>
                  ) : (
                    <>${parsedData.price}</>
                  )}
                  {parsedData.originalPrice && !parsedData.requiresManualPrice && (
                    <span className='text-muted text-decoration-line-through ms-2'>
                      ${parsedData.originalPrice}
                    </span>
                  )}
                </p>

                {/* Gallery */}
                <p>
                  <strong>Gallery:</strong> {parsedData.gallery?.length || 0} images
                </p>

                {/* Variants */}
                {parsedData.variantProps && parsedData.variantProps.length > 0 && (
                  <div className='mb-3'>
                    <strong>Variants:</strong>
                    <ul>
                      {parsedData.variantProps.map((prop) => (
                        <li key={prop.id}>
                          {prop.name}: {prop.values?.length || 0} options
                        </li>
                      ))}
                    </ul>
                    <Badge bg='info'>Generated SKUs: {parsedData.skus?.length || 0}</Badge>
                  </div>
                )}

                {/* Video */}
                {parsedData.video && (
                  <p>
                    <strong>Video:</strong> <Badge bg='success'>Detected ✓</Badge>
                  </p>
                )}

                {/* Folder Name */}
                <p className='text-muted'>
                  <small>Suggested folder: {parsedData.suggestedFolderName}</small>
                </p>

                <hr />
                <Form.Check
                  type='checkbox'
                  id='generateWithAI'
                  label='Generate description with AI'
                  checked={generateWithAI}
                  onChange={(e) => setGenerateWithAI(e.target.checked)}
                  className='mb-3'
                />
                <Form.Check
                  type='checkbox'
                  id='createInProduction'
                  label='Also create in production database'
                  checked={createInProduction}
                  onChange={(e) => setCreateInProduction(e.target.checked)}
                  className='mb-3'
                />
                {aiError && <Alert variant='warning'>{aiError}</Alert>}
                <Button variant='success' onClick={() => handleImport()} disabled={loading || isAiGeneratingRef.current}>
                  {loading ? (
                    <>
                      <Spinner animation='border' size='sm' /> Creating...
                    </>
                  ) : (
                    '✅ Create Product (Disabled)'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Modal show={showManualPriceModal} onHide={() => setShowManualPriceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manual Price Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-3'>
            This supplier page was challenge-blocked. Title and media were recovered, but price could not be trusted. Enter the product price manually to continue.
          </p>
          <Form.Group controlId='manualPrice'>
            <Form.Label>Product Price</Form.Label>
            <Form.Control
              type='text'
              inputMode='decimal'
              placeholder='19.99'
              value={manualPriceInput}
              onChange={(e) => {
                setManualPriceInput(e.target.value)
                if (manualPriceError) setManualPriceError('')
              }}
              isInvalid={Boolean(manualPriceError)}
            />
            <Form.Control.Feedback type='invalid'>
              {manualPriceError}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowManualPriceModal(false)}>
            Cancel
          </Button>
          <Button variant='warning' onClick={handleManualPriceConfirm}>
            Use Price and Create Product
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductQuickImportScreen;
